import { useEffect, useMemo, useRef, useState } from 'react';
import type { StructuralPieceId } from '@obraplan/core/domain/shared/ids';
import {
  checkGroundBeamFields,
  checkPileCapFields,
} from '@obraplan/core/domain/takeoff/foundation';
import type { Point } from '@obraplan/core/domain/takeoff/polygon';
import {
  blocoSobOPonto,
  centroDoContorno,
  encaixarNoBloco,
  proximoNomeDeBaldrame,
  proximoNomeDeBloco,
  retanguloPorDoisCantos,
} from './fundacao-da-prancha';
import {
  closesPolygon,
  distance,
  metresToPixel,
  pixelToMetres,
  resolvePoint,
  type Pixel,
} from './plan-tracer';
import type { TypologyDraft, WorkspaceAction } from './typology-draft';
import { ALTURA_DO_BLOCO_MAXIMA, useConfirmacaoDeValorGrande } from './valor-fora-do-comum';

/**
 * O que as ferramentas "Bloco" e "Baldrame" fazem na prancha (Fase 27, etapa 2 — D3, D6, F14).
 *
 * - **Bloco**: retângulo por dois cantos — arrastando ou clicando — ou polígono livre, clicando
 *   os vértices e fechando no primeiro, como o ambiente. O painel diz o nome e a altura, e
 *   "Adicionar bloco" cadastra;
 * - **Baldrame**: dois cliques, de eixo a eixo. O clique que cai dentro de um bloco vale o
 *   centro dele. O painel diz o nome e a seção, "20 × 40".
 *
 * Com a prancha parada, ou com a ferramenta do bloco sem traço pela metade, clicar num bloco o
 * seleciona e arrastá-lo o move inteiro; com a prancha parada, clicar num baldrame o seleciona
 * e arrastar uma ponta dele move só ela. O estado mora aqui, e não dentro do traçado, pelo
 * mesmo motivo do hook da estaca: o traçado já carrega o de seis ferramentas.
 */

export type FerramentaDaFundacao = 'BLOCO' | 'BALDRAME';
export type FormatoDoBloco = 'RETANGULO' | 'POLIGONO';

/** O que se pode selecionar na prancha, entre as peças desta fatia. */
export interface PecaDaFundacao {
  readonly tipo: 'BLOCO' | 'BALDRAME';
  readonly id: StructuralPieceId;
}

/** A peça na mão, arrastada: o pixel por onde ela passa até o botão subir. */
export type ArrastoDaFundacao =
  | {
      readonly tipo: 'BLOCO';
      readonly id: StructuralPieceId;
      readonly origem: Pixel;
      readonly atual: Pixel;
      /** Passou da folga do clique? Antes disso é clique, e clicar seleciona. */
      readonly moveu: boolean;
    }
  | {
      readonly tipo: 'PONTA';
      readonly id: StructuralPieceId;
      readonly ponta: 'de' | 'ate';
      readonly origem: Pixel;
      readonly atual: Pixel;
      readonly moveu: boolean;
    };

/** O traço do bloco ainda não cadastrado, como a prancha o desenha. */
export interface TracoDoBloco {
  readonly vertices: readonly Pixel[];
  /** Fechado o contorno é polígono cheio; aberto, é uma linha andando até o cursor. */
  readonly fechado: boolean;
  /** Os pontos clicados — as bolinhas. */
  readonly marcados: readonly Pixel[];
  /** O cursor, para o último lado ainda aberto. */
  readonly ao: Pixel | undefined;
}

/** A folga do clique, em pixels **de tela**, como a do encaixe do contorno. */
const FOLGA_DO_ARRASTO = 4;
const FOLGA_VERTICE = 10;
const FOLGA_ORTOGONAL = 8;

/** Quanto tempo depois de soltar um arrasto o clique que veio com ele ainda é dele. */
const JANELA_DO_CLIQUE_MS = 100;

/** Para conferir só o nome e a altura enquanto o contorno ainda não existe. */
const QUADRADO: readonly Point[] = [
  { x: 0, y: 0 },
  { x: 1, y: 0 },
  { x: 1, y: 1 },
  { x: 0, y: 1 },
];

/** O alvo de um arrasto, lido do elemento em que o botão desceu. */
function lerAlvo(
  alvo: Element,
):
  | { readonly tipo: 'BLOCO'; readonly id: StructuralPieceId }
  | { readonly tipo: 'PONTA'; readonly id: StructuralPieceId; readonly ponta: 'de' | 'ate' }
  | undefined {
  const ponta = alvo.closest('[data-baldrame-ponta]');
  const daPonta = ponta?.getAttribute('data-baldrame-ponta');
  const qual = ponta?.getAttribute('data-ponta');
  if (daPonta !== null && daPonta !== undefined && (qual === 'de' || qual === 'ate')) {
    return { tipo: 'PONTA', id: daPonta as StructuralPieceId, ponta: qual };
  }
  const bloco = alvo.closest('[data-bloco]')?.getAttribute('data-bloco');
  if (bloco !== null && bloco !== undefined) {
    return { tipo: 'BLOCO', id: bloco as StructuralPieceId };
  }
  return undefined;
}

export function useFundacaoNaPrancha({
  draft,
  escala,
  zoom,
  ferramenta,
  parado,
  dispatch,
  selecionar,
}: {
  draft: TypologyDraft;
  escala: number | undefined;
  zoom: number;
  /** A ferramenta ligada, se for uma destas. Parada, a prancha só seleciona e arrasta. */
  ferramenta: FerramentaDaFundacao | undefined;
  /** A prancha está parada — sem ferramenta nenhuma ligada? */
  parado: boolean;
  dispatch: React.Dispatch<WorkspaceAction>;
  selecionar: (peca: PecaDaFundacao) => void;
}) {
  const blocos = useMemo(
    () => (draft.pileCaps ?? []).filter((bloco) => bloco.deletedAt === undefined),
    [draft.pileCaps],
  );
  const baldrames = useMemo(
    () => (draft.groundBeams ?? []).filter((baldrame) => baldrame.deletedAt === undefined),
    [draft.groundBeams],
  );

  const [formato, setFormato] = useState<FormatoDoBloco>('RETANGULO');
  /** Os cantos do retângulo ou os vértices do polígono, em pixel da prancha. */
  const [pontos, setPontos] = useState<readonly Pixel[]>([]);
  const [fechado, setFechado] = useState(false);
  /** O retângulo arrastado, antes de o botão subir. */
  const [gesto, setGesto] = useState<
    { readonly origem: Pixel; readonly atual: Pixel; readonly moveu: boolean } | undefined
  >(undefined);
  const [nomeDigitadoDoBloco, setNomeDigitadoDoBloco] = useState<string | undefined>(undefined);
  /** Metros — o concreto do bloco, do fundo ao topo. */
  const [alturaDoBloco, setAlturaDoBloco] = useState(0.6);

  /** Os dois pontos do eixo, em pixel da prancha. */
  const [eixo, setEixo] = useState<readonly Pixel[]>([]);
  const [nomeDigitadoDoBaldrame, setNomeDigitadoDoBaldrame] = useState<string | undefined>(
    undefined,
  );
  /** Centímetros, como a obra fala a seção: "20 × 40". */
  const [larguraEmCm, setLarguraEmCm] = useState(20);
  const [alturaEmCm, setAlturaEmCm] = useState(40);

  const [cursor, setCursor] = useState<Pixel | undefined>(undefined);
  const [arrastando, setArrastando] = useState<ArrastoDaFundacao | undefined>(undefined);
  /** Quando o último arrasto terminou — o clique que vem junto com ele não é de ninguém. */
  const cliqueDoArrasto = useRef<number | undefined>(undefined);

  // Trocar de tipologia ou de ferramenta descarta o que estava na mão: o ponto marcado é do
  // desenho e da ferramenta que saíram da mesa.
  const emDesenho = draft.unitTypeId;
  useEffect(() => {
    setPontos([]);
    setFechado(false);
    setGesto(undefined);
    setEixo([]);
    setCursor(undefined);
    setArrastando(undefined);
  }, [emDesenho, ferramenta]);

  const nomeDoBloco = nomeDigitadoDoBloco ?? proximoNomeDeBloco(blocos);
  const nomeDoBaldrame = nomeDigitadoDoBaldrame ?? proximoNomeDeBaldrame(baldrames);

  /** Os vértices dos blocos já desenhados, em pixel: o canto novo se encaixa neles. */
  const verticesDosBlocos = useMemo(
    (): readonly Pixel[] =>
      escala === undefined
        ? []
        : blocos.flatMap((bloco) => bloco.outline.map((ponto) => metresToPixel(ponto, escala))),
    [blocos, escala],
  );

  /** O contorno do bloco de agora, em metros — ausente enquanto o traço não fechou. */
  const contorno = useMemo((): readonly Point[] | undefined => {
    if (escala === undefined) return undefined;
    if (formato === 'RETANGULO') {
      const [a, b] = pontos;
      return a === undefined || b === undefined
        ? undefined
        : retanguloPorDoisCantos(pixelToMetres(a, escala), pixelToMetres(b, escala));
    }
    return fechado ? pontos.map((ponto) => pixelToMetres(ponto, escala)) : undefined;
  }, [escala, formato, pontos, fechado]);

  /** O que impede cadastrar o bloco de agora — o painel diz, e o botão espera. */
  const impedimentoDoBloco = useMemo((): string | undefined => {
    const conferido = checkPileCapFields(
      { name: nomeDoBloco, outline: contorno ?? QUADRADO, height: alturaDoBloco },
      blocos,
    );
    return conferido.ok ? undefined : conferido.error.message;
  }, [nomeDoBloco, contorno, alturaDoBloco, blocos]);

  /** O eixo do baldrame de agora, em metros — ausente enquanto faltar um dos pontos. */
  const eixoEmMetros = useMemo((): { readonly from: Point; readonly to: Point } | undefined => {
    const [a, b] = eixo;
    if (escala === undefined || a === undefined || b === undefined) return undefined;
    return { from: pixelToMetres(a, escala), to: pixelToMetres(b, escala) };
  }, [escala, eixo]);

  const impedimentoDoBaldrame = useMemo((): string | undefined => {
    const conferido = checkGroundBeamFields(
      {
        name: nomeDoBaldrame,
        from: eixoEmMetros?.from ?? { x: 0, y: 0 },
        to: eixoEmMetros?.to ?? { x: 1, y: 0 },
        width: larguraEmCm / 100,
        height: alturaEmCm / 100,
      },
      baldrames,
    );
    return conferido.ok ? undefined : conferido.error.message;
  }, [nomeDoBaldrame, eixoEmMetros, larguraEmCm, alturaEmCm, baldrames]);

  /** Há traço pela metade? Aí o clique é do traço, e a peça que está embaixo deixa passar. */
  const emTraco = pontos.length > 0 || eixo.length > 0 || gesto !== undefined;

  /** Os blocos respondem a clique com a prancha parada, ou com a ferramenta dele sem traço. */
  const blocosClicaveis = parado || (ferramenta === 'BLOCO' && !emTraco);
  /** Os baldrames só com a prancha parada: o clique do baldrame novo cai sobre os velhos. */
  const baldramesClicaveis = parado;

  /** Onde o canto do bloco cai: encaixado no vértice de um bloco, ou onde foi clicado. */
  function cantoDoBloco(bruto: Pixel, anterior?: Pixel): Pixel {
    return resolvePoint(bruto, {
      vertices: [...verticesDosBlocos, ...pontos],
      vertexTolerance: FOLGA_VERTICE / zoom,
      ...(anterior === undefined
        ? {}
        : { previous: anterior, orthogonalTolerance: FOLGA_ORTOGONAL / zoom }),
    });
  }

  /** Onde o ponto do eixo cai: no centro do bloco em que o clique caiu, ou no esquadro. */
  function pontoDoEixo(bruto: Pixel, anterior?: Pixel): Pixel {
    if (escala === undefined) return bruto;
    const bloco = blocoSobOPonto(pixelToMetres(bruto, escala), blocos);
    if (bloco !== undefined) return metresToPixel(centroDoContorno(bloco.outline), escala);
    return anterior === undefined
      ? bruto
      : resolvePoint(bruto, {
          previous: anterior,
          vertexTolerance: 0,
          orthogonalTolerance: FOLGA_ORTOGONAL / zoom,
        });
  }

  /** O clique no vazio da prancha, com uma das ferramentas ligadas. */
  function clicarNaPrancha(bruto: Pixel): void {
    if (escala === undefined) return;

    if (ferramenta === 'BLOCO') {
      if (formato === 'RETANGULO') {
        const canto = cantoDoBloco(bruto);
        const primeiro = pontos[0];
        if (pontos.length === 1 && primeiro !== undefined) {
          // O segundo clique no mesmo canto não é retângulo de tamanho zero.
          if (distance(primeiro, canto) < FOLGA_VERTICE / zoom) return;
          setPontos([primeiro, canto]);
          return;
        }
        // Zero ou dois cantos: o clique começa outro retângulo.
        setPontos([canto]);
        return;
      }
      if (fechado) {
        setFechado(false);
        setPontos([cantoDoBloco(bruto)]);
        return;
      }
      if (closesPolygon(bruto, pontos, FOLGA_VERTICE / zoom)) {
        setFechado(true);
        return;
      }
      setPontos((atual) => [...atual, cantoDoBloco(bruto, atual[atual.length - 1])]);
      return;
    }

    if (ferramenta === 'BALDRAME') {
      const primeiro = eixo[0];
      if (eixo.length === 1 && primeiro !== undefined) {
        const ponto = pontoDoEixo(bruto, primeiro);
        // O segundo clique no mesmo lugar — no mesmo bloco — não é eixo de comprimento zero.
        if (distance(primeiro, ponto) < FOLGA_VERTICE / zoom) return;
        setEixo([primeiro, ponto]);
        return;
      }
      setEixo([pontoDoEixo(bruto)]);
    }
  }

  /** O botão desceu na prancha: com o retângulo ligado, pode ser o começo de um arrasto. */
  function apertarNaPrancha(ponto: Pixel): void {
    if (ferramenta !== 'BLOCO' || formato !== 'RETANGULO' || escala === undefined) return;
    setGesto({ origem: ponto, atual: ponto, moveu: false });
  }

  /** O botão desceu num bloco ou na ponta de um baldrame: se andar, a peça vai junto. */
  function apertarNaPeca(alvo: Element, ponto: Pixel): boolean {
    if (escala === undefined) return false;
    const lido = lerAlvo(alvo);
    if (lido === undefined) return false;
    if (lido.tipo === 'BLOCO' ? !blocosClicaveis : !baldramesClicaveis) return false;
    setArrastando({ ...lido, origem: ponto, atual: ponto, moveu: false });
    return true;
  }

  /** O mouse andou: a peça na mão, o retângulo arrastado e o segmento vivo. */
  function acompanhar(ponto: Pixel): boolean {
    if (arrastando !== undefined) {
      setArrastando({
        ...arrastando,
        atual: ponto,
        moveu: arrastando.moveu || distance(arrastando.origem, ponto) > FOLGA_DO_ARRASTO / zoom,
      });
      return true;
    }
    if (gesto !== undefined) {
      setGesto({
        ...gesto,
        atual: ponto,
        moveu: gesto.moveu || distance(gesto.origem, ponto) > FOLGA_DO_ARRASTO / zoom,
      });
      return true;
    }
    // O segmento vivo só existe com traço pela metade; fora disso o cursor não custa render.
    const aberto =
      (ferramenta === 'BLOCO' &&
        ((formato === 'RETANGULO' && pontos.length === 1) ||
          (formato === 'POLIGONO' && pontos.length > 0 && !fechado))) ||
      (ferramenta === 'BALDRAME' && eixo.length === 1);
    if (aberto) setCursor(ponto);
    return false;
  }

  /** O botão subiu: grava o movimento, ou fecha o retângulo arrastado. */
  function soltar(ponto: Pixel): boolean {
    const corrente = arrastando;
    if (corrente !== undefined) {
      setArrastando(undefined);
      if (escala === undefined) return true;
      // Soltar onde apertou é clique, e clicar seleciona: a peça não sai do lugar.
      if (!corrente.moveu && distance(corrente.origem, ponto) <= FOLGA_DO_ARRASTO / zoom) {
        return true;
      }
      cliqueDoArrasto.current = Date.now();

      if (corrente.tipo === 'BLOCO') {
        const bloco = blocos.find((candidato) => candidato.id === corrente.id);
        if (bloco === undefined) return true;
        const dx = (ponto.x - corrente.origem.x) * escala;
        const dy = (ponto.y - corrente.origem.y) * escala;
        dispatch({
          type: 'UPDATE_PILE_CAP',
          pieceId: corrente.id,
          outline: bloco.outline.map((vertice) => ({ x: vertice.x + dx, y: vertice.y + dy })),
        });
      } else {
        const destino = encaixarNoBloco(pixelToMetres(ponto, escala), blocos);
        dispatch({
          type: 'UPDATE_GROUND_BEAM',
          pieceId: corrente.id,
          ...(corrente.ponta === 'de' ? { from: destino } : { to: destino }),
        });
      }
      return true;
    }

    const desenhado = gesto;
    if (desenhado !== undefined) {
      setGesto(undefined);
      // Sem andar, é clique: o clique que vem a seguir marca o canto.
      if (!desenhado.moveu && distance(desenhado.origem, ponto) <= FOLGA_DO_ARRASTO / zoom) {
        return false;
      }
      cliqueDoArrasto.current = Date.now();
      const a = cantoDoBloco(desenhado.origem);
      const b = cantoDoBloco(ponto);
      if (distance(a, b) >= FOLGA_VERTICE / zoom) setPontos([a, b]);
      return true;
    }
    return false;
  }

  /**
   * O clique que o fim do arrasto dispara é do arrasto; consome-o, e só uma vez.
   *
   * O navegador o entrega no mesmo instante em que o botão sobe; a janela existe para que um
   * arrasto cujo clique nunca veio — soltar fora da prancha — não engula o clique seguinte,
   * que é de outra pessoa.
   */
  function foiCliqueDoArrasto(): boolean {
    const desde = cliqueDoArrasto.current;
    cliqueDoArrasto.current = undefined;
    return desde !== undefined && Date.now() - desde < JANELA_DO_CLIQUE_MS;
  }

  /** O bloco de 12 m de altura pede conferência: o aviso aparece e o cadastro vale no segundo clique. */
  const confirmaAlturaDoBloco = useConfirmacaoDeValorGrande(alturaDoBloco, ALTURA_DO_BLOCO_MAXIMA);

  function adicionarBloco(): void {
    if (contorno === undefined || impedimentoDoBloco !== undefined) return;
    if (!confirmaAlturaDoBloco.confirmar()) return;
    confirmaAlturaDoBloco.limpar();
    dispatch({ type: 'ADD_PILE_CAP', name: nomeDoBloco, outline: contorno, height: alturaDoBloco });
    setPontos([]);
    setFechado(false);
    setGesto(undefined);
    setCursor(undefined);
    setNomeDigitadoDoBloco(undefined);
  }

  function adicionarBaldrame(): void {
    if (eixoEmMetros === undefined || impedimentoDoBaldrame !== undefined) return;
    dispatch({
      type: 'ADD_GROUND_BEAM',
      name: nomeDoBaldrame,
      from: eixoEmMetros.from,
      to: eixoEmMetros.to,
      width: larguraEmCm / 100,
      height: alturaEmCm / 100,
    });
    setEixo([]);
    setCursor(undefined);
    setNomeDigitadoDoBaldrame(undefined);
  }

  /** O traço do bloco como o desenho o mostra: o retângulo, ou o polígono andando. */
  const traco = ((): TracoDoBloco | undefined => {
    if (ferramenta !== 'BLOCO') return undefined;
    if (formato === 'RETANGULO') {
      const [a, b] = pontos;
      if (gesto?.moveu === true) {
        return {
          vertices: retanguloPorDoisCantos(gesto.origem, gesto.atual),
          fechado: true,
          marcados: [gesto.origem],
          ao: undefined,
        };
      }
      if (a !== undefined && b !== undefined) {
        return {
          vertices: retanguloPorDoisCantos(a, b),
          fechado: true,
          marcados: [a, b],
          ao: undefined,
        };
      }
      if (a !== undefined) {
        return {
          vertices: cursor === undefined ? [] : retanguloPorDoisCantos(a, cursor),
          fechado: true,
          marcados: [a],
          ao: undefined,
        };
      }
      return undefined;
    }
    if (pontos.length === 0) return undefined;
    return { vertices: pontos, fechado, marcados: pontos, ao: fechado ? undefined : cursor };
  })();

  return {
    ferramenta,
    blocos,
    baldrames,
    // O bloco
    formato,
    escolherFormato: (novo: FormatoDoBloco) => {
      setFormato(novo);
      setPontos([]);
      setFechado(false);
      setGesto(undefined);
      setCursor(undefined);
    },
    cantos: pontos.length,
    fechado,
    contorno,
    traco,
    nomeDoBloco,
    digitarNomeDoBloco: setNomeDigitadoDoBloco,
    alturaDoBloco,
    mudarAlturaDoBloco: setAlturaDoBloco,
    impedimentoDoBloco,
    avisoDaAlturaDoBloco: confirmaAlturaDoBloco.aviso,
    adicionarBloco,
    fecharContorno: () => {
      if (formato === 'POLIGONO' && pontos.length >= 3) setFechado(true);
    },
    desfazerPonto: () => {
      if (fechado) setFechado(false);
      else setPontos((atual) => atual.slice(0, -1));
    },
    refazerBloco: () => {
      setPontos([]);
      setFechado(false);
      setGesto(undefined);
      setCursor(undefined);
    },
    // O baldrame
    eixo,
    cursor,
    eixoEmMetros,
    nomeDoBaldrame,
    digitarNomeDoBaldrame: setNomeDigitadoDoBaldrame,
    larguraEmCm,
    mudarLarguraEmCm: setLarguraEmCm,
    alturaEmCm,
    mudarAlturaEmCm: setAlturaEmCm,
    impedimentoDoBaldrame,
    adicionarBaldrame,
    refazerBaldrame: () => {
      setEixo([]);
      setCursor(undefined);
    },
    // O que os eventos do desenho chamam
    arrastando,
    blocosClicaveis,
    baldramesClicaveis,
    apertarNaPrancha,
    apertarNaPeca,
    acompanhar,
    soltar,
    cancelarArrasto: () => {
      setArrastando(undefined);
      setGesto(undefined);
    },
    foiCliqueDoArrasto,
    clicarNaPrancha,
    clicarNoBloco: (id: StructuralPieceId) => {
      if (foiCliqueDoArrasto()) return;
      selecionar({ tipo: 'BLOCO', id });
    },
    clicarNoBaldrame: (id: StructuralPieceId) => {
      if (foiCliqueDoArrasto()) return;
      selecionar({ tipo: 'BALDRAME', id });
    },
  };
}

export type FundacaoNaPrancha = ReturnType<typeof useFundacaoNaPrancha>;
